import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { getConfig } from '../config';
import type { CountryCode } from '../models/Currency';

export interface ShippingPolicy {
  countryCode: CountryCode;
  name: string;
  currencyCode: string;
  freeShippingThreshold?: number;
  shippingCost: number;
  deliveryDuration: {
    minDays: number;
    maxDays: number;
  };
  enabled: boolean;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;
}

export interface ShippingPolicyResponse {
  countryCode: CountryCode;
  name: string;
  freeShippingThreshold?: number;
  shippingCost: number;
  deliveryDuration: {
    minDays: number;
    maxDays: number;
  };
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateShippingPolicyRequest {
  countryCode: CountryCode;
  shippingDuration: {
    minDay: number;
    maxDay: number;
  };
}

export interface DeleteShippingPolicyRequest {
  countryCode: CountryCode;
}

export interface ApiShippingConfig {
  countryCode: CountryCode;
  currencyCode: string;
  shippingFee: number;
  freeShippingThreshold?: number;
  shippingDuration: {
    minDay: number;
    maxDay: number;
  };
}

export interface GetShippingPoliciesResponse {
  key: string;
  version: number;
  config: ApiShippingConfig[];
}

export interface GlobalShippingSettings {
  systemMessage: string | null;
  globalShippingDelayDay: number | null;
}

export interface UpdateGlobalShippingSettingsRequest {
  systemMessage: string | null;
  globalShippingDelayDay: number | null;
}

export interface DownloadParxlLabelRequest {
  trackingIDs: string[];
}

export interface ParxlLabelItem {
  plsShipmentReference: string;
  pdfData: string;
}

export interface DownloadParxlLabelResponse {
  success: boolean;
  labels?: ParxlLabelItem[];
}

export class ShippingClient {
  private readonly headers: Record<string, string>;

  constructor(private readonly apiUrl: string) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Origin: window.location.origin,
      'x-api-key': getConfig().apiKey,
    };
  }

  private async makeRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    data?: any,
  ): Promise<AxiosResponse<T>> {
    const config = {
      method,
      url: `${this.apiUrl}${endpoint}`,
      headers: this.headers,
      ...(data && { data }),
    };

    return axios(config);
  }

  async getShippingPolicies(): Promise<ShippingPolicy[]> {
    try {
      const response = await this.makeRequest<GetShippingPoliciesResponse>('GET', '/shippingPolicies');

      return response.data.config.map((config) => ({
        countryCode: config.countryCode,
        name: `${config.countryCode} Shipping Policy`, // Default name since API doesn't provide name field
        currencyCode: config.currencyCode,
        shippingCost: config.shippingFee,
        ...(config.freeShippingThreshold !== undefined && { freeShippingThreshold: config.freeShippingThreshold }),
        deliveryDuration: {
          minDays: config.shippingDuration.minDay,
          maxDays: config.shippingDuration.maxDay,
        },
        enabled: true, // Default to enabled since API doesn't provide enabled field
        createdAt: undefined,
        updatedAt: undefined,
      }));
    } catch (error) {
      console.error('Error fetching shipping policies:', error);
      throw error;
    }
  }

  async createOrUpdateShippingPolicy(request: CreateShippingPolicyRequest): Promise<void> {
    try {
      await this.makeRequest('POST', '/shippingPolicies', request);
    } catch (error) {
      console.error('Error creating/updating shipping policy:', error);
      throw error;
    }
  }

  async deleteShippingPolicy(request: DeleteShippingPolicyRequest): Promise<void> {
    try {
      await this.makeRequest('DELETE', '/shippingPolicies', request);
    } catch (error) {
      console.error('Error deleting shipping policy:', error);
      throw error;
    }
  }

  async getGlobalShippingSettings(): Promise<GlobalShippingSettings> {
    try {
      const response = await this.makeRequest<GlobalShippingSettings>('GET', '/shippingPolicies/global');
      return response.data;
    } catch (error) {
      console.error('Error fetching global shipping settings:', error);
      throw error;
    }
  }

  async updateGlobalShippingSettings(request: UpdateGlobalShippingSettingsRequest): Promise<GlobalShippingSettings> {
    try {
      const response = await this.makeRequest<GlobalShippingSettings>('PUT', '/shippingPolicies/global', request);
      return response.data;
    } catch (error) {
      console.error('Error updating global shipping settings:', error);
      throw error;
    }
  }

  async downloadParxlLabels(request: DownloadParxlLabelRequest): Promise<DownloadParxlLabelResponse> {
    try {
      const response = await this.makeRequest<DownloadParxlLabelResponse>('POST', '/shipping/parxl/labels', request);
      return response.data;
    } catch (error) {
      console.error('Error downloading Parxl labels:', error);
      throw error;
    }
  }
}

export const shippingClient = new ShippingClient(getConfig().apiUrl);
